/**
 * Admin Overview tile — "Top waitlisted states".
 *
 * Reads the shipping waitlist roll-up and surfaces the top 5 states by
 * pending signups (highest unmet demand). Tells the admin exactly which
 * state to license a pharmacy partner in next for maximum unlocked
 * revenue. Self-hides when there are zero pending signups (fresh installs
 * / no out-of-coverage traffic yet) so we don't waste vertical space.
 *
 * Each row is clickable → navigates to the Pharmacy Partners admin tab
 * so the admin can update a pharmacy's `ships_to_states` and the
 * auto-notify pipeline fires off launch-day emails to the waitlist.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { MapPin, TrendingUp, Loader2 } from "lucide-react";

export default function WaitlistedStatesCard({ onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    API.get("/admin/shipping-waitlist")
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const ranked = (() => {
    const by = (data && data.by_state) || {};
    return Object.entries(by)
      .map(([st, c]) => ({ state: st, pending: c.pending || 0, notified: c.notified || 0 }))
      .filter((r) => r.pending > 0)
      .sort((a, b) => b.pending - a.pending)
      .slice(0, 5);
  })();

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 flex items-center gap-2 text-xs text-slate-400" data-testid="waitlisted-states-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading waitlist demand…
      </div>
    );
  }
  if (ranked.length === 0) return null;

  const maxPending = ranked[0].pending;

  return (
    <div
      className="bg-white dark:bg-slate-900 rounded-xl border border-amber-200 dark:border-amber-900/40 p-5"
      data-testid="waitlisted-states-card"
    >
      <div className="flex items-start gap-2 mb-3">
        <span className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
          <TrendingUp className="w-4.5 h-4.5 text-amber-600" />
        </span>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 dark:text-white text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Top waitlisted states
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            Where unmet demand is highest. License a pharmacy partner here next to unlock {ranked.reduce((s, r) => s + r.pending, 0)} pending order{ranked.reduce((s, r) => s + r.pending, 0) === 1 ? "" : "s"}.
          </p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {ranked.map((r) => {
          const widthPct = Math.max(8, Math.round((r.pending / maxPending) * 100));
          return (
            <li key={r.state}>
              <button
                type="button"
                onClick={() => onJump?.("pharmacies")}
                className="w-full flex items-center gap-3 text-left rounded-lg px-2 py-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                data-testid={`waitlist-row-${r.state}`}
                title={`${r.pending} pending signup${r.pending === 1 ? "" : "s"} for ${r.state}. Click to open Pharmacy Partners and update coverage.`}
              >
                <span className="inline-flex items-center gap-1 text-xs font-mono font-bold text-amber-700 dark:text-amber-300 w-12">
                  <MapPin className="w-3 h-3 opacity-60" />
                  {r.state}
                </span>
                <div className="flex-1 h-2.5 rounded-full bg-amber-100 dark:bg-amber-950/60 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-900 dark:text-white w-12 text-right tabular-nums" data-testid={`waitlist-count-${r.state}`}>
                  {r.pending}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
